import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { finalize, map, tap } from 'rxjs';

import { ToasterService } from './toaster/toaster.service';
import { ICustomResponseModel } from '../models/custom-error-response.model';
import { SpinnerService } from '../spinner.service';
import { AuthService } from './auth.service';
import { HttpErrorStatusCode } from '../../shared/enums/http-request-status-code.enum';

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next('');
      observer.complete();
    } else {
      const reader = new FileReader();
      reader.onload = event => {
        observer.next((<any>event.target).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

function handleError(toasterService: ToasterService, response: HttpResponseBase, text: string): Observable<any> {
  const errorResponse: ICustomResponseModel = {
    statusCode: response.status,
    message: text
  };
  toasterService.errorMethod(errorResponse);
  return throwError(() => response);
}

function transformBlobToJson(response: HttpResponseBase): Promise<string> {
  return new Promise(resolve => {
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;
    blobToText(responseBlob)
      .pipe(
        map(responseText => {
          if (responseText !== null) {
            const responseObject: ICustomResponseModel = JSON.parse(responseText);
            if (responseObject.showToasterMessage != null && responseObject.showToasterMessage)
              return responseObject.detail;
            return responseObject.message ? responseObject.message : responseObject.title;
          }
          return null;
        })
      )
      .subscribe(res => resolve(res));
  });
}

/**
 * Functional HTTP interceptor for Angular 19.
 * Adds Authorization header, manages spinner visibility, and handles HTTP errors.
 */
export const appHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const spinner = inject(SpinnerService);
  const toasterService = inject(ToasterService);
  const authService = inject(AuthService);

  /* Added delay (800 milliseconds) to open spinner, to prevent blinking screen when response comes very fast
     If response comes within 800 milliseconds then spinner will not show
  */
  const timer = setTimeout(() => spinner.visibility.next(true), 800);

  const accessToken = authService.getAccessToken();
  if (accessToken && req.url.indexOf('?post_type=') === -1) {
    const headers = req.headers.set('Authorization', `Bearer ${accessToken}`);
    req = req.clone({ headers });
  }

  return next(req).pipe(
    finalize(() => {
      clearTimeout(timer);
      spinner.visibility.next(false);
    }),
    tap({
      error: (error) => {
        if (error.status === HttpErrorStatusCode.UNAUTHORIZED) {
          authService.login();
          return;
        }
        if (error instanceof HttpErrorResponse) {
          transformBlobToJson(error).then((res: any) => {
            handleError(toasterService, error, res);
          });
        }
      }
    })
  );
};
